/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-use-before-define */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'

const FeatureFlagsContext = createContext({ flags: {}, ready: false })

const readAllFlags = () => {
  if (typeof posthog?.featureFlags?.getFlagVariants === 'function') {
    return posthog.featureFlags.getFlagVariants() || {}
  }

  if (typeof posthog?.getAllFlags === 'function') {
    return posthog.getAllFlags() || {}
  }

  return {}
}

export const initFeatureFlags = async ({
  apiKey,
  apiHost = 'https://us.i.posthog.com',
  distinctId,
  personProperties,
  groupType,
  groupKey,
  groupProperties,
  bootstrapTimeoutMs = 2500,
}) => {
  if (!apiKey) {
    return ({ children }) => (
      <FeatureFlagsContext.Provider value={{ flags: {}, ready: true }}>
        {children}
      </FeatureFlagsContext.Provider>
    )
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    bootstrap: {
      distinctID: distinctId,
    },
    loaded: ph => {
      if (distinctId) {
        ph.identify(distinctId, personProperties)
      }
      if (groupType && groupKey) {
        ph.group(groupType, String(groupKey), groupProperties)
      }
    },
  })

  // Wait for the first flag payload (or timeout) so initial render sees populated flags.
  await new Promise(resolve => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      resolve()
    }

    posthog.onFeatureFlags(() => finish())

    setTimeout(finish, bootstrapTimeoutMs)
  })

  const initialFlags = readAllFlags()

  return ({ children }) => (
    <FeatureFlagsProvider initialFlags={initialFlags}>
      {children}
    </FeatureFlagsProvider>
  )
}

export const FeatureFlagsProvider = ({ initialFlags = {}, children }) => {
  const [flags, setFlags] = useState(initialFlags)

  useEffect(() => {
    const unsubscribe = posthog.onFeatureFlags(() => {
      setFlags(readAllFlags())
    })

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    }
  }, [])

  const value = useMemo(() => ({ flags, ready: true }), [flags])

  return (
    <PostHogProvider client={posthog}>
      <FeatureFlagsContext.Provider value={value}>
        {children}
      </FeatureFlagsContext.Provider>
    </PostHogProvider>
  )
}

export const useFlags = () => {
  const { flags } = useContext(FeatureFlagsContext)
  return flags
}

export const withFeatureFlags = Component => {
  const Wrapped = props => {
    const flags = useFlags()
    return <Component {...props} flags={flags} />
  }

  Wrapped.displayName = `withFeatureFlags(${Component.displayName ||
    Component.name ||
    'Component'})`

  return Wrapped
}

export default FeatureFlagsContext
